import { Assets, Matrix, Rectangle, Spritesheet, Texture, type FillStyle, type Renderer, type SpritesheetData } from "pixi.js";
import { Angle } from "@common/utils/math";
import type { Vector } from "@common/utils/vector";
import type { Orientation } from "@common/typings";
import textureBudget from "./stylizedEnvironmentBudget.json";

interface StylizedManifest {
    revision: number;
    baseBytes: number;
    sheets: SpritesheetData[];
}

export let stylizedEnvironmentReady = false;
let stoneBridgeDeck: Texture | undefined;
let stoneBridgeContact: Texture | undefined;
export const paintedInteriorMaterials = new Map<string, Texture>();

/** Reuse every existing sprite container, anchor and floor/roof state. No extra world drawables. */
export async function loadStylizedEnvironment(textures: Record<string, Texture>, renderer: Renderer): Promise<void> {
    if (new URLSearchParams(location.search).get("momoProps") === "legacy") return;
    const manifest = await Assets.load<StylizedManifest>("/img/momo/stylized/manifest.json");
    if (manifest.baseBytes > textureBudget.maxBaseBytes || manifest.sheets.length > textureBudget.maxAtlases) {
        throw new Error("Stylized environment exceeds its shared texture budget");
    }
    const replacements: Record<string, Texture> = {};
    for (const data of manifest.sheets) {
        if (!data.meta.image) throw new Error("Stylized atlas image is missing");
        const atlas = await Assets.load<Texture>(data.meta.image);
        await renderer.prepare.upload(atlas);
        const frames = await new Spritesheet(atlas, data).parse();
        for (const [name, frame] of Object.entries(frames)) {
            if (/^momo_(?:oak|ceramic|industrial)_floor_tile$/.test(name)) {
                paintedInteriorMaterials.set(name, frame);
                continue;
            }
            // This material replaces the bridge's existing ground polygon fill;
            // it does not create another world sprite or another texture allocation.
            if (name === "momo_stone_bridge_deck") {
                stoneBridgeDeck = frame;
                continue;
            }
            if (name === "momo_stone_bridge_approach") {
                stoneBridgeContact = frame;
                continue;
            }
            const original = textures[name] ?? textures[name.replace(/^momo_sand_/, "")];
            if (!original) continue; // Some faction props only exist in other map modes.
            replacements[name] = new Texture({
                source: frame.source,
                frame: frame.frame.clone(),
                rotate: frame.rotate,
                orig: original.orig.clone(),
                trim: frame.trim ? scaledPaintTrim(frame, original) : undefined,
                label: `momo-stylized:${name}`
            });
        }
    }
    Object.assign(textures, replacements);
    stylizedEnvironmentReady = true;
    document.documentElement.dataset.momoEnvironmentRevision = String(manifest.revision);
    document.documentElement.dataset.momoStylizedFrames = String(Object.keys(replacements).length);
    document.documentElement.dataset.momoStylizedBaseBytes = String(manifest.baseBytes);
}

/** Transparent margins can leave the atlas while their native world-space anchors stay fixed. */
export function scaledPaintTrim(frame: Texture, original: Texture): Rectangle | undefined {
    const trim = frame.trim;
    if (!trim) return undefined;
    const x = original.orig.width / frame.orig.width, y = original.orig.height / frame.orig.height;
    return new Rectangle(trim.x * x, trim.y * y, trim.width * x, trim.height * y);
}

/** Applied once while building the world terrain and the M-map; no per-frame work. */
export function paintedStoneBridge(position: Vector, orientation: Orientation, scale: number): FillStyle | undefined {
    if (!stylizedEnvironmentReady || !stoneBridgeDeck) return undefined;
    return {
        texture: stoneBridgeDeck,
        textureSpace: "global",
        matrix: stoneBridgeMatrix(stoneBridgeDeck.width, stoneBridgeDeck.height, position, orientation, scale)
    };
}

export function stoneBridgeMatrix(textureWidth: number, textureHeight: number, position: Vector, orientation: Orientation, scale: number): Matrix {
    const width = 44.77 * scale, height = 211.1 * scale;
    return new Matrix().scale(width / textureWidth, height / textureHeight)
        .translate(-width / 2, -height / 2)
        .rotate(Angle.orientationToRotation(orientation))
        .translate(position.x * scale, position.y * scale);
}

export const STONE_BRIDGE_CONTACT = { width: 50, height: 10, offset: 105.7 } as const;

/** Ground wear sits beneath the existing end stones; it adds no paved extension. */
export function paintedStoneBridgeContact(position: Vector, orientation: Orientation, scale: number, end: -1 | 1): FillStyle | undefined {
    if (!stylizedEnvironmentReady || !stoneBridgeContact) return undefined;
    return {
        texture: stoneBridgeContact,
        textureSpace: "global",
        matrix: stoneBridgeContactMatrix(stoneBridgeContact.width, stoneBridgeContact.height, position, orientation, scale, end)
    };
}

export function stoneBridgeContactMatrix(textureWidth: number, textureHeight: number, position: Vector, orientation: Orientation, scale: number, end: -1 | 1): Matrix {
    const { width, height, offset } = STONE_BRIDGE_CONTACT;
    return new Matrix().scale(width * scale / textureWidth, height * scale / textureHeight)
        .translate(-width * scale / 2, -height * scale / 2)
        .rotate(end === 1 ? Math.PI : 0)
        .translate(0, end * offset * scale)
        .rotate(Angle.orientationToRotation(orientation))
        .translate(position.x * scale, position.y * scale);
}
